// =============================================================================
// PEDRA — src/components/StockTable.tsx
// Tabela de CHAPAS de estoque. Mesmas colunas da tabela de pecas, menos fita de
// borda e cor, e com quantidade vazia significando estoque ILIMITADO (∞).
// =============================================================================

import { useCallback, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import type { Id, Settings, StockSheet, Unit } from '../core/types';
import { isImperial, parseDimension } from '../core/units';
import { validateStock } from '../core/validation';
import { EditableGrid, formatDimensionExact, parseIntegerText } from './EditableGrid';
import type { ColumnDef } from './EditableGrid';

// -----------------------------------------------------------------------------
// UNIDADE VISIVEL
//
// Mesma regra da tabela de pecas (ver PartsTable): a chapa e' onde o engano de
// unidade custa mais caro — uma chapa de 3,07 "milimetros" nao acusa erro de
// digitacao, so devolve um aproveitamento de 1,5%. As tabelas repetem a
// declaracao de unidade de proposito, cada uma no seu proprio cabecalho.
// -----------------------------------------------------------------------------

/** Sigla curta da unidade, como aparece no cabecalho: `(m)`, `(mm)`, `(pol)`. */
const UNIT_ABBR: Readonly<Record<Unit, string>> = Object.freeze({
  mm: 'mm',
  cm: 'cm',
  m: 'm',
  in: 'pol',
  ft: 'pé',
});

/** Nome por extenso, para os tooltips e o selo da secao. */
const UNIT_NAME: Readonly<Record<Unit, string>> = Object.freeze({
  mm: 'milímetros',
  cm: 'centímetros',
  m: 'metros',
  in: 'polegadas',
  ft: 'pés',
});

/** A MESMA medida (3,07 m) escrita em cada unidade: o exemplo do erro real. */
const UNIT_EXAMPLE: Readonly<Record<Unit, string>> = Object.freeze({
  mm: '3070',
  cm: '307',
  m: '3,07',
  in: '120 7/8"',
  ft: `10' 0 7/8"`,
});

/** Par comprimento/largura de uma chapa inteira, na unidade ativa. */
const PASTE_EXAMPLE: Readonly<Record<Unit, string>> = Object.freeze({
  mm: '2750\t1830',
  cm: '275\t183',
  m: '2,75\t1,83',
  in: '108\t72',
  ft: '9\t6',
});

/**
 * Cabecalho de coluna de medida em DUAS linhas: rotulo em cima, unidade
 * embaixo. `ColumnDef.header` e' texto puro, entao a quebra vai no proprio
 * texto — `grid.css` a interpreta com `white-space: pre-line`.
 */
function dimensionHeader(label: string, unit: Unit): string {
  return `${label}\n(${UNIT_ABBR[unit]})`;
}

/**
 * Selo da unidade ativa ao lado do titulo da secao. Com `onOpenSettings` ele e'
 * um botao que abre o seletor de unidade em um clique; sem o callback, vira
 * legenda com `title` — visivel do mesmo jeito, que e' o essencial.
 */
function UnitChip(props: { unit: Unit; onOpenSettings?: (() => void) | undefined }): ReactElement {
  const { unit, onOpenSettings } = props;
  const text = `Medidas em ${UNIT_NAME[unit]} (${UNIT_ABBR[unit]})`;
  const base = `Tudo que você digitar nesta tabela é lido em ${UNIT_NAME[unit]} — ex.: ${UNIT_EXAMPLE[unit]}.`;

  if (onOpenSettings) {
    return (
      <button
        type="button"
        className="grid-panel__unit"
        onClick={onOpenSettings}
        title={`${base} Clique para trocar a unidade.`}
      >
        <span aria-hidden="true">📐</span>
        {text}
      </button>
    );
  }
  return (
    <span
      className="grid-panel__unit grid-panel__unit--static"
      title={`${base} Para trocar, abra Opções → Configurações → Unidade.`}
    >
      <span aria-hidden="true">📐</span>
      {text}
    </span>
  );
}

export interface StockTableProps {
  stock: readonly StockSheet[];
  settings: Settings;
  materials: readonly string[];
  onChange(id: Id, patch: Partial<StockSheet>): void;
  onAdd(): void;
  onDelete(ids: Id[]): void;
  onDuplicate(ids: Id[]): void;
  onPaste(text: string): void;
  onReorder(ids: Id[], toIndex: number): void;
  onClear(): void;
  /**
   * Abre as configuracoes no seletor de unidade. Opcional: quando o pai nao
   * passa nada, o selo da unidade continua visivel, so nao clicavel.
   */
  onOpenSettings?(): void;
  highlightedStockId?: Id | null;
  /** Chapas ja consumidas pelo ultimo resultado (extensao opcional). */
  usedStockIds?: readonly Id[];
  onHoverStock?(id: Id | null): void;
}

/** Linha "em branco": alimenta a linha vazia automatica do fim da grade. */
function isBlankStock(s: StockSheet): boolean {
  return s.length === 0 && s.width === 0 && s.label.trim() === '';
}

export function StockTable(props: StockTableProps): ReactElement {
  const {
    stock,
    settings,
    materials,
    onChange,
    onAdd,
    onDelete,
    onDuplicate,
    onPaste,
    onReorder,
    onClear,
    onOpenSettings,
    highlightedStockId,
    usedStockIds,
    onHoverStock,
  } = props;

  const [selected, setSelected] = useState<Id[]>([]);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [confirmClear, setConfirmClear] = useState(false);

  const unit = settings.unit;

  // ---------------------------------------------------------------------------
  // COLUNAS
  // ---------------------------------------------------------------------------
  const columns = useMemo<Array<ColumnDef<StockSheet>>>(() => {
    // Igual a tabela de pecas: a grade mostra a medida EXATA digitada. O
    // arredondamento de `settings.decimalPlaces` fica para o diagrama, as
    // estatisticas e os relatorios.
    const showDim = (v: number): string => (v > 0 ? formatDimensionExact(v, unit) : '');
    const dimHint = `Medida inválida. Digite o valor em ${UNIT_NAME[unit]} (${UNIT_ABBR[unit]}) com vírgula decimal (ex.: ${UNIT_EXAMPLE[unit]}) ou como fração de polegada (ex.: 16 1/2").`;
    // Mesma regra da tabela de peças: teclado numérico no métrico, teclado
    // completo no imperial (`16 1/2"` precisa de barra e aspas).
    const dimInputMode = isImperial(unit) ? 'text' : 'decimal';

    const lengthCol: ColumnDef<StockSheet> = {
      key: 'length',
      header: dimensionHeader('COMPRIMENTO', unit),
      width: 'minmax(74px, 0.7fr)',
      align: 'end',
      editor: 'dimension',
      inputMode: dimInputMode,
      title: `Comprimento da chapa bruta, em ${UNIT_NAME[unit]} (${UNIT_ABBR[unit]}) — ex.: ${UNIT_EXAMPLE[unit]}.`,
      errorHint: dimHint,
      format: (s) => showDim(s.length),
      toInput: (s) => showDim(s.length),
      parse: (raw) => {
        const text = raw.trim();
        if (text === '') return { length: 0 };
        const v = parseDimension(text, unit);
        if (v === null || v < 0) return null;
        return { length: v };
      },
      validate: (s) =>
        !s.enabled || isBlankStock(s) || s.length > 0
          ? null
          : 'Informe um comprimento maior que zero.',
    };

    const widthCol: ColumnDef<StockSheet> = {
      key: 'width',
      header: dimensionHeader('LARGURA', unit),
      width: 'minmax(48px, 0.55fr)',
      align: 'end',
      editor: 'dimension',
      inputMode: dimInputMode,
      title: `Largura da chapa bruta, em ${UNIT_NAME[unit]} (${UNIT_ABBR[unit]}) — ex.: ${UNIT_EXAMPLE[unit]}.`,
      errorHint: dimHint,
      format: (s) => showDim(s.width),
      toInput: (s) => showDim(s.width),
      parse: (raw) => {
        const text = raw.trim();
        if (text === '') return { width: 0 };
        const v = parseDimension(text, unit);
        if (v === null || v < 0) return null;
        return { width: v };
      },
      validate: (s) =>
        !s.enabled || isBlankStock(s) || s.width > 0 ? null : 'Informe uma largura maior que zero.',
    };

    const cols: Array<ColumnDef<StockSheet>> =
      settings.dimensionOrder === 'widthFirst' ? [widthCol, lengthCol] : [lengthCol, widthCol];

    cols.push({
      key: 'qty',
      header: 'QTD',
      width: 'minmax(32px, 0.3fr)',
      align: 'end',
      editor: 'integer',
      title: 'Quantidade disponível. Deixe vazio para estoque ilimitado (∞).',
      errorHint:
        'Quantidade inválida. Digite um número inteiro (ex.: 4) ou deixe vazio para estoque ilimitado.',
      format: (s) => (s.qty === null ? '∞' : String(s.qty)),
      toInput: (s) => (s.qty === null ? '' : String(s.qty)),
      parse: (raw) => {
        const text = raw.trim();
        if (text === '' || text === '∞') return { qty: null };
        const n = parseIntegerText(text);
        return n === null ? null : { qty: n };
      },
      validate: (s) =>
        !s.enabled || isBlankStock(s) || s.qty === null || s.qty > 0
          ? null
          : 'Quantidade zero: esta chapa não entra no cálculo.',
    });

    cols.push({
      key: 'label',
      header: 'NOME',
      width: 'minmax(36px, 2fr)',
      editor: 'text',
      title: 'Nome da chapa, exibido no cabeçalho de cada diagrama.',
      format: (s) => s.label,
      parse: (raw) => ({ label: raw.trimStart() }),
    });

    if (settings.considerMaterial) {
      cols.push({
        key: 'material',
        header: 'MATERIAL',
        width: 'minmax(48px, 2fr)',
        editor: 'text',
        datalist: materials,
        title:
          'Material da chapa. Escolha um já cadastrado ou digite um novo. Só recebe peças do mesmo material.',
        format: (s) => s.material,
        parse: (raw) => ({ material: raw.trim() }),
        validate: (s) =>
          !s.enabled || isBlankStock(s) || s.material.trim() !== ''
            ? null
            : 'Escolha o material da chapa.',
      });
    }

    if (settings.considerGrain) {
      cols.push({
        key: 'orientation',
        header: 'VEIO',
        width: '28px',
        align: 'center',
        editor: 'grain',
        title: 'Direção do veio da chapa: indiferente, ao longo da largura ou do comprimento.',
        format: (s) => String(s.orientation),
        parse: (raw) => {
          const n = Number(raw);
          if (n === 0 || n === 1 || n === 2) return { orientation: n };
          return null;
        },
      });
    }

    cols.push({
      key: 'enabled',
      header: 'ATIVA',
      width: '30px',
      align: 'center',
      editor: 'checkbox',
      title: 'Chapa desativada continua salva, mas fica fora do cálculo.',
      format: (s) => (s.enabled ? '1' : '0'),
      parse: (raw) => ({ enabled: raw === '1' }),
    });

    return cols;
  }, [
    unit,
    settings.dimensionOrder,
    settings.considerMaterial,
    settings.considerGrain,
    materials,
  ]);

  // ---------------------------------------------------------------------------
  // ERROS POR LINHA
  // ---------------------------------------------------------------------------
  const rowIssues = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const s of stock) {
      if (!s.enabled || isBlankStock(s)) continue;
      const msgs = validateStock(s, settings);
      if (msgs.length > 0) out[s.id] = msgs;
    }
    return out;
  }, [stock, settings]);

  // ---------------------------------------------------------------------------
  // RESUMO E ACOES
  // ---------------------------------------------------------------------------
  const filledRows = useMemo(() => stock.filter((s) => !isBlankStock(s)).length, [stock]);
  // A linha em branco automatica do fim da grade NAO entra na conta.
  const totalSheets = useMemo(() => {
    let total = 0;
    let unlimited = false;
    for (const s of stock) {
      if (!s.enabled || isBlankStock(s)) continue;
      if (s.qty === null) unlimited = true;
      else total += Math.max(0, s.qty);
    }
    return unlimited ? '∞' : String(total);
  }, [stock]);
  const errorCount = Object.keys(rowIssues).length;

  const handleDelete = useCallback(
    (ids: Id[]) => {
      onDelete(ids);
      setSelected((prev) => prev.filter((id) => !ids.includes(id)));
    },
    [onDelete],
  );

  const applyPaste = useCallback(() => {
    const text = pasteText;
    setPasteText('');
    setPasteOpen(false);
    if (text.trim() === '') return;
    // Descarta as linhas em branco do fim antes de anexar as novas, senao a
    // linha vazia automatica ficaria encalhada no meio da tabela.
    const trailing: Id[] = [];
    for (let i = stock.length - 1; i >= 0; i--) {
      const s = stock[i];
      if (!isBlankStock(s)) break;
      trailing.push(s.id);
    }
    if (trailing.length > 0) onDelete(trailing);
    onPaste(text);
  }, [pasteText, stock, onDelete, onPaste]);

  const handleClear = useCallback(() => {
    if (!confirmClear) {
      setConfirmClear(true);
      return;
    }
    setConfirmClear(false);
    setSelected([]);
    onClear();
  }, [confirmClear, onClear]);

  return (
    <section className="grid-panel" aria-labelledby="grid-stock-title">
      <header className="grid-panel__head">
        <h2 className="grid-panel__title" id="grid-stock-title">
          Chapas
        </h2>
        <span className="grid-panel__count">
          {filledRows} {filledRows === 1 ? 'linha' : 'linhas'} · {totalSheets}{' '}
          {totalSheets === '1' ? 'chapa' : 'chapas'}
        </span>
        <UnitChip unit={unit} onOpenSettings={onOpenSettings} />
        {errorCount > 0 ? (
          <span className="grid-panel__badge" role="status">
            {errorCount} {errorCount === 1 ? 'linha com erro' : 'linhas com erro'}
          </span>
        ) : null}
      </header>

      <EditableGrid<StockSheet>
        columns={columns}
        rows={stock}
        getRowId={(s) => s.id}
        onChangeRow={(id, patch) => onChange(id, patch)}
        onAddRow={onAdd}
        onDeleteRows={handleDelete}
        onDuplicateRows={onDuplicate}
        onPaste={onPaste}
        onReorder={onReorder}
        selectedIds={selected}
        onSelectionChange={setSelected}
        rowIssues={rowIssues}
        emptyMessage="Nenhuma chapa ainda. Informe as medidas da chapa bruta."
        ariaLabel="Tabela de chapas de estoque"
        highlightedRowId={highlightedStockId ?? null}
        onHoverRow={onHoverStock}
        isRowEmpty={isBlankStock}
        usedRowIds={usedStockIds}
      />

      <div className="grid-toolbar" role="toolbar" aria-label="Ações da tabela de chapas">
        <button type="button" className="btn btn--sm" onClick={onAdd} title="Adicionar linha (Ctrl+Enter)">
          + Linha
        </button>
        <button
          type="button"
          className="btn btn--sm"
          onClick={() => onDuplicate(selected)}
          disabled={selected.length === 0}
          title="Duplicar as linhas selecionadas (Ctrl+Shift+D)"
        >
          ⧉ Duplicar
        </button>
        <button
          type="button"
          className="btn btn--sm btn--danger"
          onClick={() => handleDelete(selected)}
          disabled={selected.length === 0}
          title="Remover as linhas selecionadas (Ctrl+Del)"
        >
          ✕ Remover
        </button>
        <button
          type="button"
          className="btn btn--sm"
          onClick={() => setPasteOpen((v) => !v)}
          aria-expanded={pasteOpen}
          title="Colar uma lista copiada do Excel ou do Google Planilhas"
        >
          Colar do Excel
        </button>
        <span className="grid-toolbar__spacer" />
        <button
          type="button"
          className={`btn btn--sm${confirmClear ? ' btn--danger' : ' btn--ghost'}`}
          onClick={handleClear}
          onBlur={() => setConfirmClear(false)}
          title="Apagar todas as chapas"
        >
          {confirmClear ? 'Confirmar limpeza?' : 'Limpar'}
        </button>
      </div>

      {pasteOpen ? (
        <div className="grid-paste">
          <label className="grid-paste__label" htmlFor="grid-paste-stock">
            Cole abaixo (uma chapa por linha: comprimento, largura, quantidade, nome, material). As
            medidas serão lidas em {UNIT_NAME[unit]} ({UNIT_ABBR[unit]}):
          </label>
          <textarea
            id="grid-paste-stock"
            className="grid-paste__area"
            rows={4}
            value={pasteText}
            spellCheck={false}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder={`${PASTE_EXAMPLE[unit]}\t4\tChapa inteira\tMDF 15 mm`}
          />
          <div className="grid-paste__actions">
            <button type="button" className="btn btn--sm btn--primary" onClick={applyPaste}>
              Adicionar linhas
            </button>
            <button
              type="button"
              className="btn btn--sm btn--ghost"
              onClick={() => {
                setPasteText('');
                setPasteOpen(false);
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
